import { useState, useCallback, useRef, useMemo, lazy, Suspense } from "react";
import { useDropzone } from "react-dropzone";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { toast } from "sonner";
import { Button } from "@cieda/djayma-ui";
import {
  Upload,
  Wand2,
  Eraser,
  SlidersHorizontal,
  Loader2,
  X,
  Check,
  RotateCcw,
  ImagePlus,
} from "lucide-react";
import { IMAGE_PRESETS, type PresetKey } from "./image-presets";
import { useBackgroundRemoval } from "./useBackgroundRemoval";
import { useImageEnhance } from "./useImageEnhance";

// Lazy-load the heavy Filerobot editor (konva + styled-components)
const FilerobotImageEditor = lazy(
  () => import("react-filerobot-image-editor")
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MarketingImageEditorProps {
  /** Which preset to use for crop/resize defaults */
  readonly preset: PresetKey;
  /** Current image URL (for edit mode) */
  readonly value?: string;
  /** Called with the final processed image URL and optionally the blob */
  readonly onChange: (imageUrl: string, imageBlob?: Blob) => void;
  /** Max file size in MB */
  readonly maxSizeMB?: number;
  /** Optional label shown above the dropzone */
  readonly label?: string;
  /** Additional CSS classes */
  readonly className?: string;
}

type EditorMode = "idle" | "editing" | "comparing";

// ---------------------------------------------------------------------------
// Filerobot editor config
// ---------------------------------------------------------------------------

function getEditorConfig(preset: PresetKey) {
  const p = IMAGE_PRESETS[preset];

  return {
    // Tabs
    tabsIds: ["Adjust", "Finetune", "Filters", "Annotate", "Resize"],
    defaultTabId: "Adjust" as const,
    // Crop presets
    Crop: {
      presetsItems: [
        {
          titleKey: p.cropLabel,
          ratio: p.aspectRatio,
          icon: undefined,
        },
      ],
      autoResize: true,
    },
    // Finetune (brightness, contrast, etc.)
    Finetune: {
      tools: [
        "Brightness",
        "Contrast",
        "Saturation",
        "Warmth",
        "HSV",
        "Blur",
      ] as const,
    },
    // Annotate (text overlays, shapes)
    Annotate: {
      tools: ["Text", "Arrow", "Rect", "Ellipse", "Line"] as const,
    },
    // Resize
    Resize: {
      width: p.width,
      height: p.height,
      lockAspectRatio: true,
    },
    // Theme
    theme: {
      palette: {
        "bg-primary": "#292524",
        "bg-secondary": "#44403c",
        "accent-primary": "#d97706",
        "accent-primary-active": "#b45309",
        "icons-primary": "#fafaf9",
        "icons-secondary": "#a8a29e",
        "borders-primary": "#57534e",
        "borders-secondary": "#78716c",
        "txt-primary": "#fafaf9",
        "txt-secondary": "#d6d3d1",
        "txt-placeholder": "#a8a29e",
        "btn-primary-text": "#ffffff",
        warning: "#f59e0b",
        error: "#ef4444",
        success: "#22c55e",
      },
      typography: {
        fontFamily: "'Plus Jakarta Sans', sans-serif",
      },
    },
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MarketingImageEditor({
  preset,
  value,
  onChange,
  maxSizeMB = 5,
  label,
  className = "",
}: MarketingImageEditorProps) {
  const presetConfig = IMAGE_PRESETS[preset];
  const editorConfig = useMemo(() => getEditorConfig(preset), [preset]);

  // State
  const [mode, setMode] = useState<EditorMode>(value ? "idle" : "idle");
  const [originalBlob, setOriginalBlob] = useState<Blob | null>(null);
  const [originalUrl, setOriginalUrl] = useState<string>(value ?? "");
  const [editedUrl, setEditedUrl] = useState<string>(value ?? "");
  const [editedBlob, setEditedBlob] = useState<Blob | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showCompare, setShowCompare] = useState(false);

  // AI hooks
  const {
    removeBackground,
    isProcessing: isRemovingBg,
    progress: bgProgress,
  } = useBackgroundRemoval();
  const { autoEnhance, isProcessing: isEnhancing } = useImageEnhance();

  // Refs
  const revokeRef = useRef<string[]>([]);

  // Cleanup object URLs on unmount
  const trackUrl = useCallback((url: string) => {
    revokeRef.current.push(url);
    return url;
  }, []);

  // ---- Dropzone ----

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;

      const maxBytes = maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        toast.error(
          `Le fichier dépasse ${maxSizeMB} Mo (${(file.size / 1024 / 1024).toFixed(1)} Mo).`
        );
        return;
      }

      const url = trackUrl(URL.createObjectURL(file));
      setOriginalBlob(file);
      setOriginalUrl(url);
      setEditedUrl(url);
      setEditedBlob(file);
      setMode("idle");
      setShowCompare(false);
    },
    [maxSizeMB, trackUrl]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
      "image/avif": [".avif"],
    },
    maxFiles: 1,
    multiple: false,
  });

  // ---- Editor callbacks ----

  const handleEditorSave = useCallback(
    (editedImageObject: { imageBase64?: string }) => {
      if (!editedImageObject.imageBase64) return;

      // Convert base64 to blob
      const base64 = editedImageObject.imageBase64;
      const byteString = atob(base64.split(",")[1]);
      const mimeString = base64.split(",")[0].split(":")[1].split(";")[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });
      const url = trackUrl(URL.createObjectURL(blob));

      setEditedUrl(url);
      setEditedBlob(blob);
      setShowEditor(false);
      toast.success("Modifications appliquées");
    },
    [trackUrl]
  );

  // ---- AI actions ----

  const handleRemoveBg = useCallback(async () => {
    const blob = editedBlob ?? originalBlob;
    if (!blob) return;

    const result = await removeBackground(blob);
    if (result) {
      const url = trackUrl(URL.createObjectURL(result));
      setEditedUrl(url);
      setEditedBlob(result);
      toast.success("Fond supprimé avec succès");
    } else {
      toast.error("Erreur lors de la suppression du fond");
    }
  }, [editedBlob, originalBlob, removeBackground, trackUrl]);

  const handleAutoEnhance = useCallback(async () => {
    const blob = editedBlob ?? originalBlob;
    if (!blob) return;

    const result = await autoEnhance(blob);
    if (result) {
      const url = trackUrl(URL.createObjectURL(result));
      setEditedUrl(url);
      setEditedBlob(result);
      toast.success("Image améliorée automatiquement");
    } else {
      toast.error("Erreur lors de l'amélioration");
    }
  }, [editedBlob, originalBlob, autoEnhance, trackUrl]);

  // ---- Reset ----

  const handleReset = useCallback(() => {
    if (originalUrl) {
      setEditedUrl(originalUrl);
      setEditedBlob(originalBlob);
      setShowCompare(false);
      toast.info("Image réinitialisée");
    }
  }, [originalUrl, originalBlob]);

  // ---- Confirm ----
  // Upload the edited blob to Supabase Storage via /api/cms/upload BEFORE
  // calling onChange. Without this step the form receives a blob: URL that
  // gets persisted to the DB, where it's permanently broken for all other
  // users (audit finding S-H8).

  const [isUploading, setIsUploading] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (!editedBlob || !editedUrl) return;
    setIsUploading(true);
    try {
      const { uploadCmsImage } = await import("@/lib/admin-api/cms");
      const { url: publicUrl } = await uploadCmsImage(editedBlob);
      onChange(publicUrl, editedBlob);
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Échec du téléversement de l'image",
      );
    } finally {
      setIsUploading(false);
    }
  }, [editedBlob, editedUrl, onChange]);

  // ---- Render ----

  const hasImage = Boolean(editedUrl);
  const isAiProcessing = isRemovingBg || isEnhancing;

  // No image yet — show dropzone
  if (!hasImage) {
    return (
      <div className={className}>
        {label && (
          <label className="mb-2 block text-sm font-medium text-stone-700">
            {label}
          </label>
        )}
        <div
          {...getRootProps()}
          className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 transition-colors ${
            isDragActive
              ? "border-amber-500 bg-amber-50"
              : "border-stone-300 bg-stone-50 hover:border-amber-400 hover:bg-amber-50/50"
          }`}
        >
          <input {...getInputProps()} />
          <ImagePlus className="mb-3 h-10 w-10 text-stone-400" />
          <p className="text-sm font-medium text-stone-600">
            {isDragActive
              ? "Déposez l'image ici..."
              : "Glissez-déposez ou cliquez pour sélectionner"}
          </p>
          <p className="mt-1 text-xs text-stone-400">
            {presetConfig.cropLabel} · JPEG, PNG, WebP · Max {maxSizeMB} Mo
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      {label && (
        <label className="mb-2 block text-sm font-medium text-stone-700">
          {label}
        </label>
      )}

      {/* Image preview */}
      <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-stone-50">
        {showCompare && originalUrl !== editedUrl ? (
          <div className="aspect-video">
            <ReactCompareSlider
              itemOne={
                <ReactCompareSliderImage
                  src={originalUrl}
                  alt="Avant"
                  style={{ objectFit: "contain" }}
                />
              }
              itemTwo={
                <ReactCompareSliderImage
                  src={editedUrl}
                  alt="Après"
                  style={{ objectFit: "contain" }}
                />
              }
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center p-4">
            <img
              src={editedUrl}
              alt="Aperçu"
              className="max-h-64 rounded-lg object-contain"
            />
          </div>
        )}

        {/* AI processing overlay */}
        {isAiProcessing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/60 backdrop-blur-sm">
            <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
            <p className="mt-3 text-sm font-medium text-white">
              {isRemovingBg
                ? `Suppression du fond... ${bgProgress}%`
                : "Amélioration en cours..."}
            </p>
            {isRemovingBg && (
              <div className="mt-2 h-1.5 w-32 overflow-hidden rounded-full bg-stone-700">
                <div
                  className="h-full rounded-full bg-amber-500 transition-all duration-300"
                  style={{ width: `${bgProgress}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Replace button */}
        <div
          {...getRootProps()}
          className="absolute right-2 top-2 cursor-pointer rounded-lg bg-white/90 p-1.5 shadow-sm transition-colors hover:bg-white"
          title="Remplacer l'image"
        >
          <input {...getInputProps()} />
          <Upload className="h-4 w-4 text-stone-600" />
        </div>
      </div>

      {/* Action toolbar */}
      <div className="mt-3 flex flex-wrap gap-2">
        {/* Edit in Filerobot */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowEditor(true)}
          disabled={isAiProcessing}
        >
          <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
          Éditer
        </Button>

        {/* AI: Remove background */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleRemoveBg}
          disabled={isAiProcessing}
        >
          <Eraser className="mr-1.5 h-3.5 w-3.5" />
          {isRemovingBg ? "Traitement..." : "Supprimer fond"}
        </Button>

        {/* AI: Auto-enhance */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAutoEnhance}
          disabled={isAiProcessing}
        >
          <Wand2 className="mr-1.5 h-3.5 w-3.5" />
          {isEnhancing ? "Amélioration..." : "Auto-améliorer"}
        </Button>

        {/* Compare before/after */}
        {originalUrl !== editedUrl && (
          <Button
            type="button"
            variant={showCompare ? "default" : "outline"}
            size="sm"
            onClick={() => setShowCompare(!showCompare)}
            disabled={isAiProcessing}
          >
            <SlidersHorizontal className="mr-1.5 h-3.5 w-3.5" />
            {showCompare ? "Masquer" : "Avant / Après"}
          </Button>
        )}

        {/* Reset */}
        {originalUrl !== editedUrl && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleReset}
            disabled={isAiProcessing}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Réinitialiser
          </Button>
        )}

        <div className="flex-1" />

        {/* Confirm */}
        <Button
          type="button"
          size="sm"
          onClick={handleConfirm}
          disabled={isAiProcessing || isUploading}
          className="bg-amber-600 text-white hover:bg-amber-700"
        >
          <Check className="mr-1.5 h-3.5 w-3.5" />
          Confirmer
        </Button>
      </div>

      {/* Filerobot Image Editor (modal) */}
      {showEditor && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
              <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
            </div>
          }
        >
          <div className="fixed inset-0 z-50">
            <FilerobotImageEditor
              source={editedUrl}
              onSave={(editedImageObject: Record<string, unknown>) =>
                handleEditorSave(
                  editedImageObject as { imageBase64?: string }
                )
              }
              onClose={() => setShowEditor(false)}
              annotationsCommon={{
                fill: "#d97706",
              }}
              Text={{
                text: "Texte",
                fonts: [
                  { label: "Plus Jakarta Sans", value: "'Plus Jakarta Sans', sans-serif" },
                  { label: "Cormorant Garamond", value: "'Cormorant Garamond', serif" },
                  { label: "Arial", value: "Arial, sans-serif" },
                ],
              }}
              savingPixelRatio={2}
              previewPixelRatio={window.devicePixelRatio}
              {...(editorConfig as Record<string, unknown>)}
            />
          </div>
        </Suspense>
      )}
    </div>
  );
}

export default MarketingImageEditor;
